import type { PresetCategory } from "@pax/api-contract/presets/v1alpha1";
import {
  focusHaloFlush,
  HapticSurface,
  MorphPanel,
  OptionRow,
  PanelHeading,
  panelCard,
  panelMotion,
  press,
  solidSurface,
  usePanelDismiss,
} from "@pax/ui";
import { SlidersHorizontal } from "lucide-react";
import { type ReactNode, useCallback, useState } from "react";
import { CATALOG_SORTS, CATEGORIES, type CatalogSort } from "../ordering";

/**
 * Narrowing the wall, as the one control a narrowed page needs that is not a
 * scenario.
 *
 * The trigger becomes the panel: one surface, and its size is the only thing
 * that changes. At the closed size the system's corner radius is exactly a
 * circle and at the open size it is the system's corner, so the shape is
 * right at both ends without anything having to travel between them. It is
 * held by its right edge, so it opens into the room it has rather than off
 * the side of the sheet.
 */
export function WallFilter({
  sort,
  categories,
  onSortChange,
  onCategoriesChange,
}: {
  sort: CatalogSort;
  categories: PresetCategory[];
  onSortChange: (sort: CatalogSort) => void;
  onCategoriesChange: (categories: PresetCategory[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  const root = usePanelDismiss<HTMLDivElement>({ open, onDismiss: close });

  const toggleCategory = (category: PresetCategory) =>
    onCategoriesChange(
      categories.includes(category)
        ? categories.filter(chosen => chosen !== category)
        : [...categories, category],
    );

  return (
    <div ref={root} className="relative size-9 shrink-0">
      <MorphPanel
        view={open ? "options" : "trigger"}
        anchor="top-left"
        growFrom="top right"
        className={`absolute top-0 right-0 z-10 ${solidSurface}`}
      >
        {open ? (
          <div className="w-56 p-1">
            <PanelHeading label="Filter" onClose={close} />
            <FilterGroup label="Order">
              {CATALOG_SORTS.map(option => (
                <OptionRow
                  key={option.id}
                  name={option.name}
                  selected={sort === option.id}
                  onSelect={() => onSortChange(option.id)}
                />
              ))}
            </FilterGroup>
            <FilterGroup label="Category">
              {CATEGORIES.map(category => (
                <OptionRow
                  key={category}
                  name={category}
                  selected={categories.includes(category)}
                  onSelect={() => toggleCategory(category)}
                />
              ))}
            </FilterGroup>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setOpen(true)}
            aria-label="Filter scenarios"
            className={`relative flex size-9 items-center justify-center ${panelMotion} ${press} ${focusHaloFlush}`}
          >
            <SlidersHorizontal className="size-4" strokeWidth={1.75} />
            <HapticSurface />
          </button>
        )}
      </MorphPanel>
    </div>
  );
}

function FilterGroup({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="mt-1 first:mt-0">
      <p className="px-2 pt-1 pb-1.5 text-xs text-muted-foreground">{label}</p>
      <div className={`${panelCard} p-1`}>{children}</div>
    </div>
  );
}
